import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Inbox, Settings2, Zap, BarChart3, Send,
  ArrowUpRight, RefreshCw, CheckCircle2,
  Cake, FileText, Receipt, HeartHandshake, BellRing,
  Sparkles, Phone as PhoneIcon, KeyRound, AlertCircle,
} from "lucide-react";
import { fmtDateTime24 } from "@/lib/time";
import MetaEmbeddedSignupButton from "@/components/MetaEmbeddedSignupButton";

/* ============================================================================
 * WhatsApp Admin — premium communication center for Viyora clinics.
 * Feb 2026 rewrite. Two-mode transparency (Viyora Managed vs Clinic Own).
 * Every card backed by a real API — no mocks.
 * ==========================================================================*/

const AUTOMATION_ICON = {
  appointment_confirmation: CheckCircle2,
  appointment_reminder:     BellRing,
  payment_reminder:         Receipt,
  recall_message:           HeartHandshake,
  birthday_wish:            Cake,
  prescription_ready:       FileText,
  invoice_notification:     Receipt,
  treatment_followup:       Sparkles,
};

function StatChip({ label, value, tone = "neutral", sub, testid }) {
  const toneMap = {
    neutral: "bg-white border-[#E7E1D3]",
    good:    "bg-emerald-50/70 border-emerald-100",
    warn:    "bg-amber-50/70 border-amber-100",
    bad:     "bg-rose-50/70 border-rose-100",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border p-4 transition-all ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className="mt-1 font-display text-2xl text-ink tabular-nums leading-none">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-ink-mid">{sub}</div>}
    </div>
  );
}

function SectionHeader({ eyebrow, title, action }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">{eyebrow}</div>
        <div className="mt-1 font-display text-xl text-ink">{title}</div>
      </div>
      {action}
    </div>
  );
}

function fmtRel(iso) {
  if (!iso) return "just now";
  try {
    const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
    return fmtDateTime24(iso);
  } catch { return "—"; }
}

function Detail({ label, value, tone }) {
  const toneClass = tone === "good" ? "text-emerald-700"
                  : tone === "warn" ? "text-amber-700" : "text-ink";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`mt-1 font-medium ${toneClass}`}>{value ?? "—"}</div>
    </div>
  );
}

function ConnectionCard({ cfg, onReload, onConnect }) {
  const isOwn = cfg?.mode === "clinic_whatsapp";
  const meta = cfg?.meta || {};
  return (
    <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6 sm:p-7" data-testid="wa-connection-card">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1" data-testid="wa-status-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Connected
          </div>
          <div className="mt-3 font-display text-3xl text-ink leading-tight">
            {isOwn ? (meta.verified_name || "Your WhatsApp Business") : "Viyora Managed WhatsApp"}
          </div>
          <div className="mt-1 text-sm text-ink-mid">
            {isOwn
              ? <>Connected as <span className="font-mono">{meta.display_phone || "—"}</span> · Powered by Meta Cloud API</>
              : <>Patients receive messages through Viyora's managed infrastructure. Switch to your own WhatsApp Business anytime.</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onReload} className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink px-3 py-2 rounded-full border border-[#E7E1D3] hover:border-ink transition-colors" data-testid="wa-reload">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {isOwn ? (
            <Link to="/app/whatsapp/settings" className="inline-flex items-center gap-1.5 text-xs font-medium text-ink bg-white border border-[#E7E1D3] hover:border-ink px-3 py-2 rounded-full transition-colors" data-testid="wa-manage-connection">
              <Settings2 className="w-3.5 h-3.5" /> Manage Connection
            </Link>
          ) : (
            <MetaEmbeddedSignupButton onConnected={onReload} />
          )}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-[13px]" data-testid="wa-connection-details">
        <Detail label="Mode"        value={isOwn ? "Clinic WhatsApp Business" : "Viyora Managed"} />
        <Detail label="Quality"     value={meta.quality_rating || "GREEN"} tone="good" />
        <Detail label="Templates"   value={cfg?.approved_templates_count ?? 0} />
        <Detail label="Last synced" value={fmtRel(meta.last_synced_at || "")} />
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled}
      onClick={onChange} data-testid={testid}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D06A42] focus-visible:ring-offset-2 ${checked ? "bg-[#D06A42]" : "bg-[#E8EAE9]"}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
    </button>
  );
}

function AutomationsCard() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/automations");
      setRows(data.automations || []);
    } catch (e) { toast.error(apiError(e, "Failed to load automations")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, enabled: !x.enabled } : x));
    try {
      await api.patch("/whatsapp/automations", { id: r.id, enabled: !r.enabled });
      toast.success(`${r.label} ${!r.enabled ? "enabled" : "paused"}`);
    } catch (e) {
      toast.error(apiError(e, "Toggle failed"));
      setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, enabled: r.enabled } : x));
    } finally { setBusy((b) => ({ ...b, [r.id]: false })); }
  };

  return (
    <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6" data-testid="wa-automations">
      <SectionHeader eyebrow="Automation" title="Rules"
        action={<span className="text-[11px] text-ink-soft">{rows.filter((r) => r.enabled).length} of {rows.length} enabled</span>} />
      {loading ? <div className="py-8 text-center text-sm text-ink-soft"><RefreshCw className="w-4 h-4 inline-block animate-spin" /></div> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((r) => {
            const Icon = AUTOMATION_ICON[r.id] || Zap;
            return (
              <div key={r.id} data-testid={`wa-auto-${r.id}`}
                   className={`rounded-2xl border p-4 transition-all ${r.enabled ? "border-[#E7E1D3] bg-white" : "border-[#EFEADC] bg-[#FBF7EE]/60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${r.enabled ? "text-[#D06A42]" : "text-ink-soft"}`} />
                      <div className="font-display text-sm text-ink truncate">{r.label}</div>
                    </div>
                    <div className="mt-1 text-[11px] text-ink-soft leading-snug">{r.hint}</div>
                  </div>
                  <Toggle checked={r.enabled} onChange={() => toggle(r)} disabled={busy[r.id]} testid={`wa-auto-toggle-${r.id}`} />
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-ink-mid">
                  <span className="tabular-nums">{r.messages_sent} sent · last 30d</span>
                  <span>{r.last_trigger ? fmtRel(r.last_trigger) : "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AnalyticsCard() {
  const [days, setDays] = useState(7);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/stats", { params: { days } });
      setStats(data);
    } catch (e) { toast.error(apiError(e, "Failed to load stats")); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const c = stats?.counts || {};
  const total = stats?.total || 0;
  const delivered = (c.delivered || 0) + (c.read || 0);
  const failed = c.failed || 0;
  const pending = (c.queued || 0) + (c.submitted || 0) + (c.queued_manual || 0);
  const pct = (n) => total ? Math.round((n / total) * 100) : 0;

  return (
    <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6" data-testid="wa-analytics">
      <SectionHeader eyebrow="Analytics" title={`Last ${days} days`}
        action={
          <div className="inline-flex rounded-full border border-[#E7E1D3] p-0.5">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`wa-analytics-${d}d`}
                className={`text-[11px] px-3 py-1 rounded-full transition-colors ${days === d ? "bg-[#D06A42] text-white" : "text-ink-mid hover:text-ink"}`}>{d}d</button>
            ))}
          </div>
        } />
      {loading ? <div className="py-8 text-center text-sm text-ink-soft"><RefreshCw className="w-4 h-4 inline-block animate-spin" /></div> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip label="Sent"      value={total}                sub={`${days} day window`}       testid="wa-stat-sent" />
            <StatChip label="Delivered" value={delivered} tone="good" sub={`${pct(delivered)}% delivery`} testid="wa-stat-delivered" />
            <StatChip label="Read"      value={c.read || 0} tone="good" sub={`${pct(c.read || 0)}% read`} testid="wa-stat-read" />
            <StatChip label="Failed"    value={failed} tone={failed > 0 ? "bad" : "neutral"} sub={`${pct(failed)}% failed`} testid="wa-stat-failed" />
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip label="Pending"      value={pending} tone={pending > 0 ? "warn" : "neutral"} sub="Awaiting ack" testid="wa-stat-pending" />
            <StatChip label="Sent (raw)"   value={c.sent || 0} sub="Carrier ack" testid="wa-stat-sent-raw" />
            <StatChip label="Replies"      value={c.replied || 0} sub="Inbound this window" testid="wa-stat-replies" />
            <StatChip label="Success rate" value={`${Math.round((stats?.success_rate || 0) * 100)}%`} tone="good" testid="wa-stat-success" />
          </div>
        </>
      )}
    </div>
  );
}

function kindLabel(kind, status, row) {
  const k = String(kind || "").toLowerCase();
  const s = String(status || "").toLowerCase();
  // Failed row → SHOW the specific template name so operators never confuse
  // one execution's failure with a different (submitted) execution of the
  // same automation kind. Also expose the Meta error string when present.
  if (s === "failed") {
    const tpl = row?.template_name ? ` · ${row.template_name}` : "";
    const err = row?.error ? ` — ${String(row.error).slice(0, 60)}` : "";
    return `Automation failed${tpl}${err}`;
  }
  if (s === "submitted")  return "Message submitted";
  if (s === "sent")       return "Message sent";
  if (s === "delivered")  return "Message delivered";
  if (s === "read")       return "Message read";
  if (s === "expired")    return "Delivery confirmation expired";
  if (k.startsWith("appointment.confirmation")) return "Appointment confirmation sent";
  if (k.startsWith("appointment.reminder"))     return "Appointment reminder sent";
  if (k.startsWith("invoice.reminder"))         return "Payment reminder sent";
  if (k.startsWith("invoice"))                  return "Invoice shared";
  if (k.startsWith("prescription"))             return "Prescription delivered";
  if (k.startsWith("recall"))                   return "Recall sent";
  if (k.startsWith("birthday"))                 return "Birthday wish sent";
  if (k.startsWith("treatment"))                return "Follow-up sent";
  if (k.startsWith("test"))                     return "Test message sent";
  return "Message sent";
}

function ActivityCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/activity", { params: { limit: 12 } });
      setRows(data.activity || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  return (
    <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6" data-testid="wa-activity">
      <SectionHeader eyebrow="Live" title="Recent activity"
        action={<button onClick={load} className="text-[11px] text-ink-mid hover:text-ink"><RefreshCw className="w-3 h-3 inline" /></button>} />
      {loading && rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-soft"><RefreshCw className="w-4 h-4 inline-block animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-ink-soft">No activity yet — sends appear here in real time.</div>
      ) : (
        <ol className="relative pl-5 space-y-3.5 before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-px before:bg-[#E7E1D3]">
          {rows.map((r) => {
            const good = ["delivered", "read", "sent"].includes(r.status);
            const bad = r.status === "failed";
            return (
              <li key={r.id} className="relative" data-testid={`wa-activity-${r.id}`}>
                <span className={`absolute -left-[15px] top-1 w-3 h-3 rounded-full ring-4 ring-white ${good ? "bg-emerald-500" : bad ? "bg-rose-500" : "bg-amber-500"}`} />
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">
                      {kindLabel(r.kind, r.status, r)}
                      {r.template_name && <span className="text-ink-soft"> · {r.template_name}</span>}
                    </div>
                    <div className="text-[11px] text-ink-soft mt-0.5">To {r.to_phone || "—"}</div>
                  </div>
                  <div className="text-[11px] text-ink-soft whitespace-nowrap">{fmtRel(r.created_at)}</div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function ConnectMetaBanner() {
  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#D06A42] to-[#8A3F1E] text-white p-6 sm:p-7 relative overflow-hidden"
         data-testid="wa-connect-meta-banner">
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl pointer-events-none" />
      <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
            <KeyRound className="w-6 h-6" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-medium bg-white/10 border border-white/20 rounded-full px-3 py-1">
              <AlertCircle className="w-3 h-3" /> Action required · Meta credentials missing
            </div>
            <div className="mt-2.5 font-display text-2xl leading-tight">Connect Meta Cloud API</div>
            <p className="mt-1 text-sm text-white/80 max-w-lg leading-relaxed">
              You've generated a permanent access token on Meta but Viyora doesn't have it yet.
              Paste your Phone Number ID, WABA ID and Token — takes 30 seconds and we validate against Meta before saving.
            </p>
          </div>
        </div>
        <Link to="/app/whatsapp/connect-meta"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#8A3F1E] bg-white hover:bg-[#FFF7EF] px-5 py-3 rounded-full transition-all shrink-0 shadow-lg"
              data-testid="wa-connect-meta-cta">
          <KeyRound className="w-4 h-4" /> Paste credentials
        </Link>
      </div>
    </div>
  );
}

function InboxShortcut() {  return (
    <Link to="/app/whatsapp/inbox" className="block rounded-3xl bg-gradient-to-br from-[#1A362B] to-[#254B3D] text-white p-6 sm:p-7 relative overflow-hidden hover:brightness-110 transition-all" data-testid="wa-inbox-shortcut">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white/5 blur-2xl pointer-events-none" />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/60">Inbox</div>
        <div className="mt-1 font-display text-2xl">Conversations</div>
        <p className="mt-3 text-sm text-white/70 max-w-md leading-relaxed">
          Read replies, send templates, share prescriptions and invoices — without ever leaving Viyora or opening WhatsApp Business.
        </p>
        <div className="mt-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white text-[#1A362B] px-5 py-2.5 text-sm font-semibold shadow-sm" data-testid="wa-open-inbox">
            <Inbox className="w-4 h-4" /> Open Inbox <ArrowUpRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function PatientPreview({ cfg, clinicName }) {
  const isOwn = cfg?.mode === "clinic_whatsapp";
  const displayName = isOwn ? (cfg?.meta?.verified_name || clinicName) : (clinicName || "Your Clinic");
  return (
    <div className="rounded-3xl bg-white border border-[#E7E1D3] p-6" data-testid="wa-patient-preview">
      <SectionHeader eyebrow="Patient experience" title="What patients see" />
      <div className="mx-auto max-w-sm rounded-3xl bg-[#0A1F1C] p-3 shadow-lg">
        <div className="rounded-2xl bg-[#E5DDD5] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#0A1F1C]/60 mb-2">WhatsApp</div>
          <div className="rounded-2xl bg-white/80 p-3">
            <div className="text-[11px] text-emerald-700 font-medium">{displayName}</div>
            <div className="mt-1 text-sm text-ink leading-snug">
              Hi Aryan, this is a reminder for your dental appointment tomorrow at 10:30. Reply CONFIRM to lock the slot.
            </div>
            <div className="mt-1 text-[10px] text-ink-soft text-right">10:24 · ✓✓</div>
          </div>
        </div>
      </div>
      <div className="mt-4 text-[11px] text-ink-soft text-center">
        Sender: <span className="font-medium text-ink">{displayName}</span> · {isOwn ? "Your WhatsApp Business" : "Viyora Managed"}
      </div>
    </div>
  );
}

function ActionsRow({ cfg, onConnect }) {
  const isOwn = cfg?.mode === "clinic_whatsapp";
  const actions = [
    { icon: Inbox,     label: "Open Inbox",         testid: "wa-action-inbox",       href: "/app/whatsapp/inbox" },
    { icon: Zap,       label: "Automation Rules",   testid: "wa-action-automations", href: "#automations" },
    { icon: FileText,  label: "Templates",          testid: "wa-action-templates",   href: "/app/whatsapp/settings#templates" },
    { icon: BarChart3, label: "Analytics",          testid: "wa-action-analytics",   href: "#analytics" },
    { icon: Send,      label: "Campaigns",          testid: "wa-action-campaigns",   disabled: true, hint: "Coming soon" },
    isOwn
      ? { icon: Settings2, label: "Settings",             testid: "wa-action-settings", href: "/app/whatsapp/settings" }
      : { icon: PhoneIcon, label: "Connect Own WhatsApp", testid: "wa-action-connect",  onClick: onConnect },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="wa-actions">
      {actions.map(({ icon: Icon, label, testid, href, onClick, disabled, hint }) => {
        const cls = `group rounded-2xl border p-4 text-left transition-all ${disabled ? "border-[#EFEADC] bg-[#FBF7EE]/40 opacity-70 cursor-not-allowed" : "border-[#E7E1D3] bg-white hover:border-[#D06A42] hover:-translate-y-0.5 hover:shadow-sm"}`;
        const inner = (<>
          <Icon className={`w-4 h-4 ${disabled ? "text-ink-soft" : "text-[#D06A42]"}`} />
          <div className="mt-2 font-medium text-sm text-ink leading-tight">{label}</div>
          {hint && <div className="mt-0.5 text-[10px] text-ink-soft">{hint}</div>}
        </>);
        if (disabled)               return <div    key={testid} data-testid={testid} className={cls} title={hint || ""}>{inner}</div>;
        if (onClick)                return <button key={testid} onClick={onClick}    data-testid={testid} className={cls}>{inner}</button>;
        if (href?.startsWith("#"))  return <a      key={testid} href={href}          data-testid={testid} className={cls}>{inner}</a>;
        return                              <Link  key={testid} to={href}            data-testid={testid} className={cls}>{inner}</Link>;
      })}
    </div>
  );
}

export default function WhatsAppAdmin() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/config");
      setCfg(data);
    } catch (e) { toast.error(apiError(e, "Failed to load WhatsApp status")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onConnect = () => { window.location.assign("/app/whatsapp/connect"); };

  if (loading && !cfg) return (
    <div className="min-h-[60vh] grid place-items-center text-ink-soft">
      <RefreshCw className="w-5 h-5 animate-spin" />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="wa-admin-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-ink-soft">Communication</div>
        <h1 className="mt-1 font-display text-3xl sm:text-4xl text-ink">WhatsApp</h1>
        <p className="mt-2 text-sm text-ink-mid max-w-2xl">
          Reminders, confirmations, invoices and follow-ups — all handled by Viyora, sent from
          {cfg?.mode === "clinic_whatsapp" ? " your own WhatsApp Business number" : " Viyora's managed WhatsApp number"}.
        </p>
      </header>

      <ConnectionCard cfg={cfg} onReload={load} onConnect={onConnect} />
      {!(cfg?.meta?.phone_number_id_masked) && <ConnectMetaBanner />}
      <ActionsRow cfg={cfg} onConnect={onConnect} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section id="automations"><AutomationsCard /></section>
          <section id="analytics"><AnalyticsCard /></section>
        </div>
        <div className="space-y-6">
          <InboxShortcut />
          <ActivityCard />
          <PatientPreview cfg={cfg} clinicName={cfg?.meta?.verified_name} />
        </div>
      </div>
    </div>
  );
}
